/**
 * Player achievement definitions — shared by PlayerView (directory badges)
 * and PlayerProfileView (full achievement grid).
 *
 * Each achievement is a current/goal pair; cur/goal take (player, ctx) where
 * ctx = { monsters: string[], betting: object|null }.
 * Tiered achievements carry `tiers` (ascending milestones); single achievements
 * carry `goal` (one threshold; `bin` = yes/no binary).
 */

const YEAR_MS = 365.25 * 86400000;

const ZODIAC_SIGNS = [
  'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
  'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces',
  'Serpentarius',
];

const gilFmt = (n) => {
  const a = Math.abs(n);
  if (a >= 1e6) return +(n / 1e6).toFixed(1) + 'M';
  if (a >= 1e3) return Math.round(n / 1e3) + 'k';
  return '' + Math.round(n);
};

const sumGender = (p, i) => {
  if (!p.champClasses) return 0;
  let s = 0;
  for (const c of Object.values(p.champClasses)) s += c[i] || 0;
  return s;
};

export const ACHIEVEMENTS = [
  { emoji: '👑', label: 'Championships', short: 'champs', cur: (p) => p.champ || 0, tiers: [1, 25, 100, 250, 500, 1000] },
  { emoji: '🛡️', label: 'Title defenses', short: 'defenses', cur: (p) => p.champDef || 0, tiers: [10, 50, 100, 250, 500, 1000] },
  { emoji: '⚔️', label: 'Games played', short: 'games', cur: (p) => p.played || 0, tiers: [100, 1000, 5000, 10000, 25000, 50000] },
  { emoji: '🏆', label: 'Bracket wins', short: 'bracket wins', cur: (p) => p.tw || 0, tiers: [10, 50, 100, 250, 500] },
  { emoji: '🎭', label: 'Classes championed', short: 'classes', cur: (p) => (p.champClasses ? Object.keys(p.champClasses).length : 0), tiers: [5, 15, 30, 50, 70] },
  { emoji: '👹', label: 'Monsters championed', short: 'monsters', cur: (p, ctx) => ctx.monsters.filter((c) => p.champClasses && p.champClasses[c]).length, tiers: [10, 25, 40, 55] },
  { emoji: '⏳', label: 'Longest reign', short: 'in a row', cur: (p) => p.champReign || 0, tiers: [5, 10, 15, 25, 42] },
  { emoji: '🐉', label: 'Monster Quests', short: 'MQ runs', cur: (p) => (p.mqDates ? p.mqDates.length : 0), tiers: [1, 2, 3, 5] },
  { emoji: '📅', label: 'Tenure', short: 'span', cur: (p) => (p.firstChamp ? +((p.lastChamp - p.firstChamp) / YEAR_MS).toFixed(1) : 0), tiers: [1, 3, 5, 6], fmt: (v) => v + 'y' },
  { emoji: '♂', label: 'Male champion', short: 'male champs', cur: (p) => sumGender(p, 1), tiers: [10, 50, 100, 250, 500] },
  { emoji: '♀', label: 'Female champion', short: 'female champs', cur: (p) => sumGender(p, 2), tiers: [10, 50, 100, 250, 500] },
  { emoji: '🎨', label: 'Rainbow', short: 'team colors', cur: (p) => (p.champColors ? Object.keys(p.champColors).length : 0), tiers: [2, 4, 6, 8] },
  { emoji: '📆', label: 'Big year', short: 'in a year', cur: (p) => (p.champYears ? Math.max(...Object.values(p.champYears)) : 0), tiers: [10, 25, 50, 100, 250] },
  { emoji: '⭐', label: 'Zodiac', desc: 'Champion a unit of all 13 zodiac signs.', cur: (p) => ZODIAC_SIGNS.filter((s) => p.champSigns && p.champSigns[s]).length, goal: () => 13 },
  { emoji: '⚥', label: 'Versatile', desc: 'Champion as both a male and a female unit.', cur: (p) => { if (!p.champClasses) return 0; let mc = 0, fc = 0; for (const c of Object.values(p.champClasses)) { mc += c[1] || 0; fc += c[2] || 0; } return (mc > 0 && fc > 0) ? 1 : 0; }, goal: () => 1, bin: true },
  { emoji: '🌅', label: 'Day One', desc: 'Win your first title back in 2020.', cur: (p) => ((p.firstChamp && new Date(p.firstChamp).getFullYear() === 2020) ? 1 : 0), goal: () => 1, bin: true },
  { emoji: '🎯', label: 'Specialist', desc: 'Half your titles on a single class.', cur: (p) => ((p.champClasses && p.champ) ? Math.round(100 * Math.max(...Object.values(p.champClasses).map((c) => c[0])) / p.champ) : 0), goal: () => 50, fmt: (v) => v + '%' },
  { emoji: '📈', label: 'Sharpshooter', desc: 'Reach a 54% win rate over 300+ games.', cur: (p) => (p.played >= 300 ? p.winRate : 0), goal: () => 54, fmt: (v) => v + '%' },
  // Betting (gambling) — only shown when the profile has loaded betting data (`show`).
  { emoji: '💰', label: 'Millionaire', short: 'gil', show: (ctx) => !!ctx.betting, cur: (p, ctx) => ctx.betting.balance, tiers: [1000000, 3000000, 5000000, 10000000], fmt: gilFmt },
  { emoji: '🐋', label: 'Whale', short: 'wagered', show: (ctx) => !!ctx.betting, cur: (p, ctx) => ctx.betting.wagered, tiers: [1000000, 10000000, 50000000, 100000000], fmt: gilFmt },
  { emoji: '📈', label: 'In the black', short: 'ROI', show: (ctx) => !!ctx.betting && ctx.betting.bets >= 500, cur: (p, ctx) => ctx.betting.roi, tiers: [0, 5, 10], fmt: (v) => (v >= 0 ? '+' : '') + v + '%' },
];

/**
 * Compute the full achievements list with earned-state, progress, status text.
 * Sorted earned-first, then by tier level, then by progress toward next tier.
 *
 * @param {Object} player       - the player record
 * @param {string[]} monsters   - list of monster class names
 * @param {Object|null} betting - the player's betting stats, or null
 * @returns {Array} achievements with { emoji, label, desc, earned, tiered, level, maxLevel, pct, status, badge }
 */
export function achievementsFull(player, monsters, betting) {
  const ctx = { monsters: monsters || [], betting: betting || null };
  const nfmt = (v) => (typeof v === 'number' ? v.toLocaleString() : v);
  return ACHIEVEMENTS.filter((a) => !a.show || a.show(ctx)).map((a) => {
    const cur = a.cur(player, ctx), f = a.fmt || nfmt;
    if (a.tiers) {
      let level = 0;
      for (const t of a.tiers) { if (cur >= t) level++; else break; }
      const reached = level > 0 ? a.tiers[level - 1] : 0, next = level < a.tiers.length ? a.tiers[level] : null;
      const pct = next != null ? Math.max(0, Math.min(100, 100 * (cur - reached) / (next - reached))) : 100;
      return { emoji: a.emoji, label: a.label, tiered: true, level, maxLevel: a.tiers.length, earned: level > 0, next, pct,
        status: next != null ? f(cur) + ' / ' + f(next) : 'MAX · ' + f(cur),
        desc: 'Tiers: ' + a.tiers.map(f).join(' · '), badge: f(reached) + (a.short ? ' ' + a.short : '') };
    }
    const goal = a.goal(player, ctx), earned = cur >= goal;
    return { emoji: a.emoji, label: a.label, desc: a.desc, earned, tiered: false, pct: goal ? Math.max(0, Math.min(100, 100 * cur / goal)) : 0,
      status: a.bin ? (earned ? 'Earned' : 'Not yet') : f(cur) + ' / ' + f(goal), badge: a.label };
  }).sort((a, b) => (b.earned - a.earned) || ((b.level || 0) - (a.level || 0)) || (b.pct - a.pct));
}

/**
 * Earned-only achievements (for the directory badge row).
 */
export function achievementsEarned(player, monsters, betting) {
  return achievementsFull(player, monsters, betting).filter((a) => a.earned);
}

export { gilFmt };
